import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountType, UserAccountEmailInput } from '@lub0v/santa-utils';
import { Button, Hint, EmailInput, OtpChallenge, Stack } from '@src/components';
import { santaClient } from '@src/state/client';
import { useAppDispatch } from '@src/state/hooks';
import { loadSession } from '@src/state/sessionSlice';
import { verifyOtpForSettings } from '@src/pages/account/verifyOtp';
import { resolveApiErrorKey } from '@src/i18n/apiError';
import { translateFieldError, validateFieldError } from '@src/validation';

interface Props {
  testId?: string;
  disabled?: boolean;
  subtitle?: string;
  alreadyUsedKey?: string;
  email: string;
  onEmailChange: (email: string) => void;
  onComplete: (email: string) => void;
  shell: (title: string, subtitle: string, children: ReactNode, action?: ReactNode) => ReactNode;
}

export function NewBoxEmailStep({
  email,
  onEmailChange,
  onComplete,
  shell,
  testId = 'new-box',
  disabled = false,
  subtitle,
  alreadyUsedKey = 'newBox.email.alreadyUsed',
}: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [challenge, setChallenge] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pending = useRef(false);

  async function completeVerified() {
    const account = await santaClient.account();
    if (!account.ok || !account.data?.email) throw new Error('account unavailable');
    const verifiedEmail = account.data.email;
    const notifications = await santaClient.enableAccountNotification({
      type: AccountType.EMAIL,
      externalId: verifiedEmail,
      enabled: true,
    });
    if (!notifications.ok) throw new Error('notifications unavailable');
    await dispatch(loadSession());
    onComplete(verifiedEmail);
  }

  async function sendCode() {
    if (pending.current || disabled) return;
    const fieldError = validateFieldError(new UserAccountEmailInput(), 'email', {
      email: email.trim(),
    });
    if (!email.trim() || fieldError) {
      setError(fieldError ? translateFieldError(fieldError, t) : t('newBox.email.required'));
      return;
    }
    pending.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await santaClient.accountUpdateEmail({ email: email.trim() });
      if (!result.ok) {
        setError(
          t(
            result.error?.message === 'EMAIL_ALREADY_USED'
              ? alreadyUsedKey
              : result.error?.status === 429
                ? 'account.emailRateLimited'
                : resolveApiErrorKey(result.error?.message || 'newBox.saveFailed'),
          ),
        );
      } else if (result.data?.otpChallenge?.challengeToken) {
        setChallenge(result.data.otpChallenge.challengeToken);
      } else if (result.data?.account.email?.toLowerCase() === email.trim().toLowerCase()) {
        await completeVerified();
      } else {
        setError(t('newBox.saveFailed'));
      }
    } catch {
      setError(t('newBox.saveFailed'));
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }

  return shell(
    t('newBox.email.title'),
    subtitle ?? t('newBox.email.subtitle'),
    <Stack gap={4} wide>
      <form
        id="new-box-email-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (!challenge) void sendCode();
        }}
      >
        <Stack gap={4} wide>
          <EmailInput
            id="new-box-email"
            name="email"
            autoComplete="email"
            inputMode="email"
            autoCapitalize="none"
            spellCheck={false}
            value={email}
            disabled={disabled || busy || Boolean(challenge)}
            onValueChange={(value) => {
              setError(null);
              onEmailChange(value);
            }}
              label={t('newBox.email.label')}
            testId={`${testId}-email`}
          />
          {!challenge && (
            <Button
              type="submit"
              loading={busy}
              disabled={disabled || !email.trim()}
              testId={`${testId}-continue`}
            >
              {t('newBox.email.send')}
            </Button>
          )}
        </Stack>
      </form>
      {error && <Hint testId={`${testId}-email-error`}>{error}</Hint>}
      {challenge && (
        <>
          <OtpChallenge
            challengeToken={challenge}
            hint={t('newBox.email.codeHint', { email })}
            testId={`${testId}-email-otp`}
            onVerify={async (token, code) => {
              pending.current = true;
              setBusy(true);
              try {
                const failure = await verifyOtpForSettings(token, code);
                if (failure)
                  return failure.key === 'EMAIL_ALREADY_USED' ? { key: alreadyUsedKey } : failure;
                await completeVerified();
                return null;
              } catch {
                return { key: 'newBox.saveFailed' };
              } finally {
                pending.current = false;
                setBusy(false);
              }
            }}
          />
          <Button
            variant="ghost"
            disabled={disabled || busy}
            onClick={() => {
              setChallenge('');
              setError(null);
            }}
          >
            {t('otp.changeEmail')}
          </Button>
          <Button variant="secondary" disabled={disabled || busy} onClick={() => void sendCode()}>
            {t('newBox.email.resend')}
          </Button>
        </>
      )}
    </Stack>,
    <Stack gap={2} wide>
      <Button
        variant="secondary"
        disabled={disabled || busy}
        onClick={() => onComplete('')}
        testId={`${testId}-email-skip`}
      >
        {t('newBox.email.skip')}
      </Button>
    </Stack>,
  );
}
